import React, { useState, useEffect } from 'react';
import queryString from 'query-string';
import { navigate } from 'gatsby';
import authServiceInstaller from '../../lib/authServiceInstaller';
import {
  clearSessionAndLocalStorage,
  getFromStorage,
  removeFromStorage,
  setInStorage,
} from '../../util/storage-utils';
import routes from '../../routes';
import { useLocation } from '@reach/router';
import useContentMgmt from '../useContentMgmt';
import useFormState from '../useFormState';
import SignInValidationRules, {
  Model,
} from '../../components/installer-portal/auth/SignInValidation.rules';
import useTransition from '../useTransition';
import { AuthState } from '../../lib/AuthenticationService/types';
import microcopyGroupIds from '../../components/pge-plus-common/microcopyGroupIds';
import useUtil from './useUtil';
import apolloClient from '../../lib/apolloClient';
import {
  AUTH_ERROR,
  AuthError,
  createAuthError,
} from '../../lib/AuthenticationService/errorCodes';

type QueryParams = {
  jobRequest?: string;
  continueUrl?: string;
  lang?: string;
};

const installerEmailKey = 'installerEmail';

const useInstallerAuth = () => {
  const [authState, setState] = useState<AuthState>(
    authServiceInstaller.getState(),
  );
  const { content } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_INSTALLER_SIGN_IN,
  );
  const { hideTransition, showTransition } = useTransition();

  const location = useLocation();

  const defaultEmail: string | undefined = getFromStorage(installerEmailKey);

  const form = useFormState(
    {
      email: defaultEmail || '',
      password: '',
      rememberMe: true,
    },
    {
      validate: SignInValidationRules,
      validationContext: {
        content,
      },
    },
  );

  useEffect(() => {
    return authServiceInstaller.subscribe(setState);
  }, []);

  const queryParams = queryString.parse(location.search) as QueryParams;
  const payload = authState.tokens?.idToken?.payload as
    | { [key: string]: unknown }
    | undefined;
  const authEmail =
    typeof payload?.email === 'string' ? payload.email :
    typeof payload?.email_address === 'string' ? payload.email_address :
    typeof payload?.preferred_username === 'string' ? payload.preferred_username :
    typeof payload?.['cognito:username'] === 'string' ? payload['cognito:username'] :
    undefined;

  const handleRedirect = () => {
    if (queryParams.continueUrl) {
      void navigate(
        queryParams.continueUrl.replace(window.location.origin, ''),
      );
    } else {
      void navigate(routes.PGE_PLUS_INSTALLER_PORTAL_JOB_LISTING);
    }
  };

  //const signOut = authServiceInstaller.signOut.bind(authServiceInstaller);
  const handleSignOut = async () => {
    // Queries need to be stopped before the store is cleared, if a query is in-flight,
    // an error will be returned and we will respond with an error page
    //stopAllQueries();
    await apolloClient.stop();
    await apolloClient.clearStore();
    clearSessionAndLocalStorage();
    authServiceInstaller.signOut();
  };

  const signOut = async () => {
    await navigate(routes.PGE_PLUS_INSTALLER_PORTAL_SIGN_IN);
    await handleSignOut();
  };

  const refreshTokens = authServiceInstaller.refreshTokens.bind(
    authServiceInstaller,
  );
  const startTokenRefresher = authServiceInstaller.startTokenRefresher.bind(
    authServiceInstaller,
  );
  const stopTokenRefresher = authServiceInstaller.stopTokenRefresher.bind(
    authServiceInstaller,
  );

  const handleLogin = async (data: Model) => {
    showTransition(content.get('SUBMITTING_REQUEST') || '');
    try {
      handleRememberMe(Boolean(data.rememberMe), data.email);
      const result = await authServiceInstaller.signInWithPassword(
        data.email,
        data.password,
      );

      if (result.status === 'SUCCESS') {
        handleRedirect();
        hideTransition();
        return;
      }

      // MFA not yet supported for installer portal; surface a generic error
      form.setError('password', content.get('ERROR_AUTH_UNEXPECTED'));
      hideTransition();
    } catch (err) {
      const authError = err instanceof AuthError ? err : createAuthError(err);
      showErrorMessage(authError);
      hideTransition();
    }
  };

  const showErrorMessage = (error: Error): void => {
    const code = error instanceof AuthError ? error.code : error.message;
    switch (code) {
      case AUTH_ERROR.EMAIL_NOT_FOUND:
        form.setError('email', content.get('ERROR_AUTH_INVALID_DEFAULT'));
        break;
      case AUTH_ERROR.INVALID_PASSWORD:
        form.setError('password', content.get('ERROR_AUTH_INVALID_DEFAULT'));
        break;
      case AUTH_ERROR.TOO_MANY_ATTEMPTS:
        form.setError('password', content.get('ERROR_AUTH_EXCEEDED_ATTEMPTS'));
        break;

      default:
        console.warn('unhandled auth exception', error);
        form.setError('password', content.get('ERROR_AUTH_UNEXPECTED'));
    }
  };

  const handleRememberMe = (rememberMe: boolean, email: string): void => {
    if (rememberMe) {
      setInStorage(installerEmailKey, email);
    } else {
      removeFromStorage(installerEmailKey);
    }
  };

  const handleLoginWithPassword = form.submit(handleLogin);

  return {
    ...authState,
    form,
    authUser: authEmail ? { email: authEmail } : undefined,
    signOut,
    startTokenRefresher,
    stopTokenRefresher,
    refreshTokens,
    handleLoginWithPassword,
  };
};

export default useInstallerAuth;
